import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CalendarClock, Link2, MessageCircle, ShieldCheck, ArrowRight, Clock,
  ListChecks, CalendarCheck, Palette, Users, Sparkles, Store, Scissors,
  HeartPulse, Hand, Flower2, ChevronDown, Check, Smartphone,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { COMPANY, LEGAL_LINKS } from "@/lib/company";
import WhatsAppButton, { WhatsAppGlyph } from "@/components/WhatsAppButton";
import PublicMobileMenu from "@/components/PublicMobileMenu";

const MapPinBrand = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#34A853" d="M12 2a7 7 0 0 0-7 7c0 4.6 6.1 12.2 6.4 12.5.3.4.9.4 1.2 0C12.9 21.2 19 13.6 19 9a7 7 0 0 0-7-7z"/>
    <circle cx="12" cy="9" r="2.6" fill="#fff"/>
  </svg>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-border bg-card p-6 transition-all duration-200 hover:shadow-md hover:-translate-y-1">
    <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <h3 className="text-lg font-semibold mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ no, title, desc }) => (
  <div className="relative rounded-2xl border border-border bg-card p-7">
    <span className="font-display text-5xl font-extrabold text-primary/15">{no}</span>
    <h3 className="text-xl font-semibold mt-2 mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </div>
);

const Sector = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2.5">
    <Icon className="w-4 h-4 text-primary shrink-0" />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

const FAQ = [
  {
    q: "365Rez'i kurmak ne kadar sürer, teknik bilgi gerekir mi?",
    a: "Hiçbir teknik bilgi gerekmez. Hizmetlerinizi, fiyatlarınızı ve çalışma saatlerinizi girmeniz yeterli. Birkaç dakika içinde rezervasyon linkiniz yayında olur.",
  },
  {
    q: "Müşterilerimin randevu alması için üye olması gerekir mi?",
    a: "Hayır. Müşterileriniz sadece size özel linke girer, hizmeti ve saati seçer, ad ve telefonunu yazar; randevusu anında oluşur. Uygulama indirmek veya hesap açmak zorunda değiller.",
  },
  {
    q: "Kendi işletme adımı ve linkimi seçebilir miyim?",
    a: "Evet. Kayıt olurken işletme adınızdan size özel bir adres oluşturulur (örn. /r/kuafor-elite). Bu linki Instagram biyografinize, WhatsApp'a veya kartvizitinize koyabilirsiniz.",
  },
  {
    q: "Aynı saate iki randevu (çakışma) olabilir mi?",
    a: "Olmaz. Dolan saatler otomatik kapanır ve aynı zaman dilimine ikinci bir kayıt sistem tarafından engellenir. Takviminiz her zaman düzenli kalır.",
  },
  {
    q: "Rezervasyon sayfamı kendi markama göre özelleştirebilir miyim?",
    a: "Evet. Logo ve kapak görseli yükleyebilir, hazır renk paletlerinden veya özel bir renk seçerek rezervasyon sayfanızı markanıza uygun hale getirebilirsiniz.",
  },
  {
    q: "Hangi işletmeler 365Rez'i kullanabilir?",
    a: "Randevuyla çalışan her işletme: kuaförler, berberler, güzellik ve cilt bakım merkezleri, tırnak stüdyoları, masaj ve spa salonları, klinikler ve daha fazlası.",
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 p-5 text-left" data-testid="faq-item">
        <span className="font-semibold">{q}</span>
        <ChevronDown className={cn("w-5 h-5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && <p className="px-5 pb-5 -mt-1 text-sm text-muted-foreground leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Landing() {
  const [billing, setBilling] = useState("monthly");
  const { data: plansData } = useQuery({ queryKey: ["plans"], queryFn: () => api.get("/plans").then((r) => r.data) });
  const proMonthlyPrice = plansData?.subscriptions?.find((p) => (p.interval || "monthly") === "monthly")?.price ?? 999;
  const proYearlyPrice = plansData?.subscriptions?.find((p) => p.interval === "yearly")?.price ?? 9990;
  const { data: demo } = useQuery({ queryKey: ["demo-slug"], queryFn: () => api.get("/public/demo").then((r) => r.data), staleTime: 0, refetchOnMount: "always" });
  const demoSlug = demo?.slug || "elite-kuafor";
  return (
    <div className="min-h-screen grain-bg">
      <WhatsAppButton />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <PublicMobileMenu />
            <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
                <CalendarClock className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="max-w-[120px] sm:max-w-none">
                <span className="font-display font-bold text-xl block leading-tight">365Rez</span>
                <span className="text-[10px] text-muted-foreground leading-tight block" data-testid="brand-motto">7 Gün 24 Saat 365 Gün Rezervasyon Sistemi</span>
              </div>
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
            <Link
              to="/isletmeler"
              className="hover:text-foreground transition-colors"
              data-testid="nav-businesses-link"
            >
              İşletmeler
            </Link>
            <a href="#ozellikler" className="hover:text-foreground transition-colors">Özellikler</a>
            <a href="#entegrasyonlar" className="hover:text-foreground transition-colors">Entegrasyonlar</a>
            <a href="#nasil" className="hover:text-foreground transition-colors">Nasıl Çalışır</a>
            <a href="#kimler" className="hover:text-foreground transition-colors">Kimler İçin</a>
            <a href="#planlar" className="hover:text-foreground transition-colors" data-testid="nav-plans-link">Planlar</a>
            <a href="#sss" className="hover:text-foreground transition-colors">SSS</a>
          </div>
          <div className="flex items-center gap-2">
            <a href="https://www.instagram.com/365rezervasyon/" target="_blank" rel="noreferrer"
              aria-label="365Rez Instagram" title="365Rez Instagram" data-testid="nav-instagram-link"
              className="hidden md:inline-flex items-center shrink-0 transition-transform hover:scale-105">
              <img src="/instagram-logo.png"
                alt="365Rez Instagram" style={{ height: "36px", width: "36px", objectFit: "contain" }} />
            </a>
            <Link to="/giris"><Button className="rounded-full" data-testid="nav-login-btn">İşletme Girişi</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary bg-accent px-3 py-1.5 rounded-full mb-6">
            <Clock className="w-3.5 h-3.5" /> 7 Gün 24 Saat 365 Gün Rezervasyon Sistemi
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Randevunuz artık <span className="text-primary">tek bir linkte</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
            365Rez, randevuyla çalışan her işletme için tasarlanan online rezervasyon platformudur.
            Hizmetlerinizi fiyatlarıyla listeleyin, size özel linki paylaşın; müşterileriniz hesap
            açmadan, saniyeler içinde randevusunu oluştursun.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/kayit">
              <Button size="lg" className="rounded-full h-14 px-8 text-base group" data-testid="hero-cta-btn">
                Ücretsiz Başla
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to={`/r/${demoSlug}`}>
              <Button size="lg" variant="outline" className="rounded-full h-14 px-8 text-base" data-testid="demo-link-btn">
                Demo Rezervasyon Sayfası
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Kurulum dakikalar sürer · Kredi kartı gerekmez</p>
          </div>

          <div className="relative lg:order-1" data-testid="hero-visual">
            <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] blur-2xl" aria-hidden="true"></div>
            <img src="/hero-visual.webp" alt="365Rez online randevu sistemi görseli"
              className="relative w-full h-auto rounded-3xl shadow-2xl" loading="eager" fetchpriority="high" />
          </div>
        </div>

        {/* Promo + entegrasyon kutuları (yan yana) */}
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          <div className="relative" data-testid="hero-promo-banner">
            <div className="relative h-full rounded-3xl bg-primary text-primary-foreground p-8 shadow-xl overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" aria-hidden="true"></div>
              <div className="absolute -bottom-12 -left-8 w-44 h-44 bg-white/10 rounded-full" aria-hidden="true"></div>
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/15 px-3 py-1.5 rounded-full">
                  <Sparkles className="w-3.5 h-3.5" /> Sınırlı Süre Fırsatı
                </span>
                <h3 className="font-display text-4xl sm:text-5xl font-extrabold mt-5 leading-[1.05]">Ömür Boyu Ücretsiz</h3>
                <p className="mt-4 text-primary-foreground/80">Kredi kartı gerekmez. Kaydolun, temel özelliklerle sınırsız kullanın.</p>
                <ul className="mt-5 space-y-2.5 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0" /> Tüm özellikler açık</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0" /> Kurulum dakikalar sürer</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0" /> İstediğiniz an iptal edin</li>
                </ul>
                <Link to="/kayit" className="block mt-7">
                  <Button size="lg" className="w-full rounded-full h-14 text-base bg-white text-primary hover:bg-white/90 font-bold group" data-testid="promo-register-btn">
                    Hemen Üye Ol
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* WhatsApp + Google Haritalar entegrasyon kutusu */}
          <div className="relative rounded-3xl border border-border bg-card p-6" data-testid="hero-integration-box">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-[#25D366] flex items-center justify-center shrink-0">
                <WhatsAppGlyph className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl text-muted-foreground font-light">+</span>
              <div className="w-11 h-11 rounded-xl bg-white border border-border flex items-center justify-center shrink-0">
                <MapPinBrand className="w-6 h-6" />
              </div>
            </div>
            <h3 className="text-lg font-bold">WhatsApp & Google Haritalar Entegrasyonu</h3>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              Tüm bildirimleriniz WhatsApp'tan gider; mesajda Google Haritalar yol tarifi ve iptal/erteleme linki hazır bekler.
            </p>
            <a href={`https://wa.me/905322705174?text=${encodeURIComponent("Merhaba, 365Rez hakkında bilgi almak istiyorum.")}`}
               target="_blank" rel="noopener noreferrer"
               className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2.5 text-sm font-semibold transition-transform hover:scale-105"
               data-testid="hero-integration-wa-btn">
              <WhatsAppGlyph className="w-5 h-5" /> WhatsApp'tan Yazın
            </a>
          </div>
        </div>

        <div
          className="mt-5 overflow-hidden rounded-3xl border border-border bg-card"
          data-testid="businesses-directory-promo"
        >
          <div className="grid items-center gap-6 p-7 sm:p-9 md:grid-cols-[1fr_auto]">
            <div className="flex items-start gap-4">
              <div
                className={
                  "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 "
                  + "text-emerald-700"
                }
              >
                <Store className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                  Randevu almak için
                </p>
                <h2 className="mt-1 text-2xl font-bold">
                  365Rez işletmelerini keşfedin
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                  Online randevuya açık işletmeleri görüntüleyin ve size uygun zamanı seçin.
                </p>
              </div>
            </div>
            <Link to="/isletmeler" data-testid="businesses-directory-promo-link">
              <Button
                variant="outline"
                className="group w-full rounded-full md:w-auto"
                data-testid="businesses-directory-promo-button"
              >
                İşletmeleri Gör
                <ArrowRight
                  className={
                    "ml-2 h-4 w-4 transition-transform duration-200 "
                    + "group-hover:translate-x-1"
                  }
                />
              </Button>
            </Link>
          </div>
        </div>

        {/* Metrics strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-16">
          {[
            { v: "100+", l: "İşletme kullanıyor" },
            { v: "1000+", l: "Randevu alındı" },
            { v: "7/24", l: "Açık rezervasyon" },
            { v: "Dakikalar", l: "içinde kurulum" },
          ].map((m) => (
            <div key={m.l} className="rounded-2xl border border-border bg-card p-5 text-center">
              <p className="font-display text-2xl font-bold text-primary">{m.v}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{m.l}</p>
            </div>
          ))}
        </div>

        {/* Ücretsiz vs Pro karşılaştırma */}
        <div id="planlar" className="mt-20 scroll-mt-24" data-testid="plan-comparison">
          <div className="max-w-2xl mb-8">
            <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Planlar</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ücretsiz başlayın, hazır olunca büyüyün</h2>
            <p className="mt-3 text-muted-foreground">365Rez ömür boyu ücretsizdir. İşletmeniz büyüdükçe Pro'ya geçin.</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40">
                  <th className="text-left font-semibold p-4">Özellik</th>
                  <th className="text-center font-semibold p-4 w-28 sm:w-40 align-top">365Rez<br/>Ücretsiz</th>
                  <th className="text-center font-semibold p-4 w-36 sm:w-44 text-primary align-top">
                    365Rez<br/>Pro
                    <div className="inline-flex items-center rounded-full border border-primary/30 bg-card p-0.5 mt-2" data-testid="landing-billing-toggle">
                      <button onClick={() => setBilling("monthly")} data-testid="landing-billing-monthly"
                        className={cn("px-2.5 py-1 rounded-full text-xs font-medium transition-colors", billing === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>Aylık</button>
                      <button onClick={() => setBilling("yearly")} data-testid="landing-billing-yearly"
                        className={cn("px-2.5 py-1 rounded-full text-xs font-medium transition-colors", billing === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>Yıllık</button>
                    </div>
                    {billing === "yearly" && <span className="block text-[10px] font-bold text-emerald-600 mt-1" data-testid="yearly-badge-th">2 AY ÜCRETSİZ</span>}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Online randevu sayfası", true, true],
                  ["İşletmeye özel randevu linki", true, true],
                  ["Hizmet ve çalışma saatleri", true, true],
                  ["Randevu takvimi & müşteri kayıtları", true, true],
                  ["Instagram / WhatsApp / Web üzerinden randevu", true, true],
                  ["Temel bildirimler", true, true],
                  ["WhatsApp mesajı", "30 adet ücretsiz", "500 adet / ay dahil"],
                  ["Otomatik WhatsApp randevu hatırlatmaları", false, true],
                  ["Müşteri geri kazanımı & kayıp müşteri analizi", false, true],
                  ["WhatsApp kampanyaları", false, true],
                  ["Gelişmiş müşteri analizi & davranış geçmişi", false, true],
                  ["Randevu, müşteri ve kazanç raporları", false, true],
                  ["Personel yönetimi & personel bazlı takvim", false, true],
                  ["Kampanya ve promosyon araçları", false, true],
                  ["Şirketinize özel kişiselleştirmeler", false, true],
                ].map(([label, free, pro], i) => {
                  const Cell = (v) => v === true
                    ? <Check className="w-5 h-5 text-emerald-600 mx-auto" />
                    : v === false
                      ? <span className="text-red-400">—</span>
                      : <span className="text-xs font-medium">{v}</span>;
                  return (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="p-4 text-muted-foreground">{label}</td>
                      <td className="p-4 text-center">{Cell(free)}</td>
                      <td className="p-4 text-center bg-primary/[0.03]">{Cell(pro)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-secondary/40 font-bold">
                  <td className="p-4">{billing === "yearly" ? "Yıllık ücret" : "Aylık ücret"}</td>
                  <td className="p-4 text-center">0 TL</td>
                  <td className="p-4 text-center text-primary">
                    {billing === "yearly" ? (
                      <span>{money(proYearlyPrice)} / yıl <span className="block text-xs font-medium text-emerald-600" data-testid="yearly-discount-note">2 ay ücretsiz · ayda ~{money(Math.round(proYearlyPrice / 12))}</span></span>
                    ) : `${money(proMonthlyPrice)} / ay`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/kayit">
              <Button size="lg" className="rounded-full h-12 px-7" data-testid="comparison-free-btn">Ücretsiz Başla</Button>
            </Link>
            <Link to={`/kayit?billing=${billing}`}>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7" data-testid="comparison-pro-btn">Pro'ya Geç</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="ozellikler" className="max-w-6xl mx-auto px-5 py-16">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Özellikler</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">İşletmenizin ihtiyacı olan her şey</h2>
          <p className="mt-3 text-muted-foreground">Randevu almayı müşteriniz için kolay, yönetmeyi sizin için zahmetsiz kılar.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Feature icon={ListChecks} title="Hizmet ve Fiyat Listesi" desc="Tüm hizmetlerinizi süre ve fiyatlarıyla şık bir vitrinde gösterin. Müşteriniz ne aldığını baştan bilsin." />
          <Feature icon={Link2} title="Kişiye Özel Link" desc="Markanızı yansıtan, paylaşması kolay kendi rezervasyon adresiniz: /r/isletme-adiniz." />
          <Feature icon={Clock} title="7/24 Online Randevu" desc="Siz uyurken bile müşterileriniz uygun saati seçip randevusunu oluştursun. Fırsat kaçmasın." />
          <Feature icon={CalendarCheck} title="Akıllı Takvim Kontrolü" desc="Çalışma saatleri ve dolu saatlere göre uygun saatler otomatik hesaplanır, çift kayıt olmaz." />
          <Feature icon={MessageCircle} title="WhatsApp Bildirimi" desc="Randevu sonrası müşteriye otomatik WhatsApp onay mesajı gönderilecek altyapı hazır." />
          <Feature icon={Palette} title="Markana Özel Tema" desc="Logo ve kapak görseli yükle, tema rengini seç; rezervasyon sayfan tamamen sana ait olsun." />
          <Feature icon={Users} title="Sınırsız Müşteri" desc="Gelen tüm rezervasyonları sınırsızca kaydedin, geçmiş randevuları tek ekrandan görün." />
          <Feature icon={ShieldCheck} title="Çift Rezervasyon Koruması" desc="Aynı zaman dilimine ikinci kayıt sunucu seviyesinde engellenir. Güvenli ve tutarlı." />
          <Feature icon={Sparkles} title="Sade ve Hızlı Deneyim" desc="Müşteriniz dört basit adımda randevusunu tamamlar: hizmet, tarih, saat, bilgiler." />
        </div>
      </section>

      {/* Entegrasyonlar */}
      <section id="entegrasyonlar" className="max-w-6xl mx-auto px-5 py-16">
        <div className="rounded-3xl border border-border bg-card overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-8 sm:p-12">
              <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Entegrasyonlar</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                Tüm bildirimleriniz <span className="text-[#25D366]">WhatsApp</span> ve <span className="text-[#34A853]">Google Haritalar</span> entegrasyonlu
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Randevu oluşturulduğunda müşterinize otomatik WhatsApp mesajı gider; mesajda iptal/erteleme linki
                ve işletmenizin <b>Google Haritalar yol tarifi</b> hazır bekler. Müşteriniz hem bilgilenir hem de
                tek dokunuşla size ulaşır.
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  "Otomatik WhatsApp randevu onayı",
                  "Mesajda Google Haritalar yol tarifi linki",
                  "İptal ve erteleme bağlantıları hazır",
                  "İşletme ve platform e-posta bildirimleri",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> <span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="relative bg-accent/40 p-8 sm:p-12 flex flex-col items-center justify-center gap-6">
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-2xl bg-[#25D366] flex items-center justify-center shadow-lg">
                  <WhatsAppGlyph className="w-11 h-11 text-white" />
                </div>
                <span className="text-3xl text-muted-foreground font-light">+</span>
                <div className="w-20 h-20 rounded-2xl bg-white border border-border flex items-center justify-center shadow-lg">
                  <MapPinBrand className="w-11 h-11" />
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground text-center">WhatsApp Business API · Google Haritalar</p>
              <a href={`https://wa.me/905322705174?text=${encodeURIComponent("Merhaba, 365Rez hakkında bilgi almak istiyorum.")}`}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-semibold transition-transform hover:scale-105"
                 data-testid="integrations-whatsapp-btn">
                <WhatsAppGlyph className="w-5 h-5" /> WhatsApp'tan Yazın
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="nasil" className="max-w-6xl mx-auto px-5 py-16">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Nasıl Çalışır</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Üç adımda hazırsınız</h2>
          <p className="mt-3 text-muted-foreground">Karmaşık kurulum yok. Profilinizi oluşturun, linkinizi paylaşın, randevuları toplayın.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Step no="01" title="Profilinizi oluşturun" desc="Hizmetlerinizi ve fiyatlarınızı ekleyin, çalışma saatlerinizi belirleyin. İşletme profiliniz dakikalar içinde hazır." />
          <Step no="02" title="Linkinizi paylaşın" desc="Size özel /r/isletmeniz adresini Instagram'da, WhatsApp'ta ve profilinizde her yerde paylaşın." />
          <Step no="03" title="Randevular gelsin" desc="Müşterileriniz uygun saati seçsin, randevular panelinize düşsün. Telefon trafiği ve karışıklık yok." />
        </div>
      </section>

      {/* Sectors */}
      <section id="kimler" className="max-w-6xl mx-auto px-5 py-16">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Kimler İçin</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Randevuyla çalışan her işletme için</h2>
          <p className="mt-3 text-muted-foreground">Güzellik dünyasında en çok sevilen, randevu defteri tutan herkese yakışan bir sistem.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Sector icon={Scissors} label="Kuaförler" />
          <Sector icon={Scissors} label="Berberler" />
          <Sector icon={Flower2} label="Güzellik Salonları" />
          <Sector icon={Hand} label="Tırnak & Nail Art" />
          <Sector icon={Sparkles} label="Cilt Bakımı" />
          <Sector icon={Flower2} label="Spa & Masaj" />
          <Sector icon={HeartPulse} label="Klinik & Sağlık" />
          <Sector icon={Store} label="Ve daha fazlası" />
        </div>
      </section>

      {/* Mobil uygulama - yakında */}
      <section id="mobil" className="max-w-6xl mx-auto px-5 py-16">
        <div className="rounded-3xl border border-border bg-card p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden" data-testid="mobile-app-section">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-primary/10 rounded-full blur-2xl" aria-hidden="true" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary bg-accent px-3 py-1.5 rounded-full">
              <Smartphone className="w-3.5 h-3.5" /> Çok Yakında
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-5">Mobil uygulamamız çok yakında</h2>
            <p className="mt-4 text-muted-foreground max-w-md">
              365Rez uygulaması yakında App Store ve Google Play'de. Randevularınızı cebinizden yönetin,
              anlık bildirim alın, işletmenizi her yerden takip edin.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <div className="relative" data-testid="badge-app-store">
                <div className="flex items-center gap-3 rounded-xl bg-foreground text-background px-5 h-14 opacity-90 select-none">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" aria-hidden="true">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <div className="text-left leading-none">
                    <div className="text-[10px] opacity-80">İndirin</div>
                    <div className="text-base font-semibold">App Store</div>
                  </div>
                </div>
                <span className="absolute -top-2 -right-2 text-[10px] font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full shadow">Yakında</span>
              </div>
              <div className="relative" data-testid="badge-google-play">
                <div className="flex items-center gap-3 rounded-xl bg-foreground text-background px-5 h-14 opacity-90 select-none">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" aria-hidden="true">
                    <path d="M4 2.4v19.2c0 .8.9 1.3 1.6.9l15.4-9.6c.6-.4.6-1.4 0-1.8L5.6 1.5C4.9 1.1 4 1.6 4 2.4z" />
                  </svg>
                  <div className="text-left leading-none">
                    <div className="text-[10px] opacity-80">İndirin</div>
                    <div className="text-base font-semibold">Google Play</div>
                  </div>
                </div>
                <span className="absolute -top-2 -right-2 text-[10px] font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full shadow">Yakında</span>
              </div>
            </div>
          </div>
          <div className="hidden lg:flex justify-center relative">
            <div className="w-56 h-[420px] rounded-[2.5rem] border-8 border-foreground/90 bg-background shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 inset-x-0 flex justify-center z-10"><div className="w-24 h-6 bg-foreground/90 rounded-b-2xl" /></div>
              <div className="pt-10 px-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center"><CalendarClock className="text-primary-foreground" style={{ width: 18, height: 18 }} /></div>
                  <span className="font-display font-bold">365Rez</span>
                </div>
                <div className="h-24 rounded-xl bg-primary/10" />
                <div className="h-14 rounded-lg bg-secondary" />
                <div className="h-14 rounded-lg bg-secondary" />
                <div className="h-14 rounded-lg bg-secondary" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="sss" className="max-w-3xl mx-auto px-5 py-16">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">SSS</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Sıkça sorulan sorular</h2>
          <p className="mt-3 text-muted-foreground">Merak ettikleriniz burada. Aradığınızı bulamazsanız bir mesaj uzağınızdayız.</p>
        </div>
        <div className="space-y-3">
          {FAQ.map((f) => <FaqItem key={f.q} {...f} />)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="rounded-3xl bg-primary text-primary-foreground p-10 sm:p-16 text-center relative overflow-hidden">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">İşletmenizi bugün 365Rez'e taşıyın</h2>
          <p className="mt-4 text-primary-foreground/80 max-w-xl mx-auto">
            Kurulum dakikalar sürer; linkinizi alın, paylaşın ve randevuları toplamaya başlayın.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/kayit">
              <Button size="lg" variant="secondary" className="rounded-full h-14 px-8 text-base" data-testid="cta-register-btn">
                Ücretsiz Başla
              </Button>
            </Link>
            <Link to={`/r/${demoSlug}`}>
              <Button size="lg" variant="outline" className="rounded-full h-14 px-8 text-base bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10" data-testid="cta-demo-btn">
                Demoyu İncele
              </Button>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-primary-foreground/80">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Kredi kartı gerekmez</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Dakikalar içinde kurulum</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Taahhüt yok</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
                <CalendarClock className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-display font-bold">365Rez</span>
            </div>
            <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              {LEGAL_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="hover:text-foreground transition-colors" data-testid={`footer-legal-${l.to.replace("/", "")}`}>{l.label}</Link>
              ))}
            </nav>
          </div>
          <div className="mt-6 pt-6 border-t border-border text-sm text-muted-foreground text-center sm:text-left">
            <p>{COMPANY.legalName}</p>
            <p className="mt-1">{COMPANY.address} · {COMPANY.email}</p>
            <p className="mt-2">© {new Date().getFullYear()} 365Rez — 7 Gün 24 Saat 365 Gün Rezervasyon Sistemi · 365rez.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
